/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { RawIntlProvider } from 'react-intl';
import { I18nBlurLoaderProvider } from '@onsolve/onsolve-ui-components';

import defaultLanguage from 'common/constants/defaultLanguage';
import I18nService from 'services/i18n.service';
import localeService from 'services/locale.service';

import { I18nContext } from './context';
import { useLingui } from '@lingui/react';
import { createCompiledCatalog } from './translations.compile';

import * as plural from 'make-plural/plurals';
import { dispatchMfeEvent } from 'utils/EventBus/eventBus';

function I18nProvider({ locale, isAppLoading, children }) {
    const [language, setLanguage] = useState({});
    const [intl, setIntl] = useState(I18nService.intl);
    const [loading, setLoading] = useState(true);
    // const [languageIsSet, setLanguageIsSet] = useState(false);
    const [locales, setLocales] = useState([]);
    const { i18n } = useLingui();

    async function loadLanguage(callback) {
        try {
            setLoading(true)
            const { translations, ...data } = typeof callback === 'function' ? await callback() : callback;
            const locale = data?.isoCode ?? 'en-US';
            const config = {
                messages: translations,
                locale: locale
            };
        
            //load translations from lingui library
            const intl = I18nService.create(config);
            const { messages } = await import(`../../../../locales/${locale}/messages`);
            i18n.load(data?.isoCode ?? 'en-US', messages);

            // Grab the plurals dynamically
            i18n.loadLocaleData({
                [locale]: { plurals: plural[locale.slice(0, 2).toLowerCase()] }
            });

            //load translations from our language files in ExternalApps/i18n
            const compiledTranslations = eval(
                '(' + createCompiledCatalog(translations?.data, { strict: true }) + ')'
            );
            i18n.load(locale, compiledTranslations);
            i18n.activate(locale);
            document.dir = data?.direction ? data.direction.toLowerCase() : 'ltr';
            setIntl(intl);
            setLanguage(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    const initializeLanguages = async (lang) => {
        try {
            //lang value comes from language dropdown, if missing we are in initial render
            setLoading(true);
            // language priority is as follows:  
            // 1.language selected from dropdown
            // 2.locale from session storage
            // 3.locale from contact settings
            // 4.default locale(English)
            // 5.locale from browser settings
            const browserLang = localeService.getBrowserCultureLang();
            const sessionLang = sessionStorage.getItem('lang');
            const localeToUse = lang?.lcId || sessionLang || locale || defaultLanguage.lcId || browserLang;

            await loadLanguage(() => localeService.init(localeToUse));

            setLocales(localeService.locales);
            //some locale sources give isoCode "en-ES" (browser lang), other give lcid "en_ES" (our language dropdown), check "localeToUse" for both just in case
            const formattedLangaugeCode = localeService.getLocaleByCode(localeToUse)?.lcId ?? localeService.getLocaleById(localeToUse)?.lcId;
            sessionStorage.setItem('lang', formattedLangaugeCode);
            dispatchMfeEvent('languageChanged', {languageCode: formattedLangaugeCode });
        } catch (error) {
            console.error('Failed to fetch locales:', error);
        } finally {
            setLoading(false);
        }
     }

    useEffect(() => {
        if (!isAppLoading && locale) {
             initializeLanguages();
        }
    }, [isAppLoading, locale]);

    //allows outside components to update provider language
    const updateLanguage = (lang) => lang != language ? initializeLanguages(lang) : null

    //**unused since moving Header and language dropdown into the MFE itself**
    //allows outside Apps (like CC) to update provider lang, since this is passed as "translationCallback" as props into <Microfrontend> component
    //apps can pass in all translations from the subapp through the callback, allowing us to bypass re-fetching translation files in localService.init
    const updateLanguageWithData = useCallback(callbackResult => {
        if (typeof callbackResult === 'boolean') {
            setLoading(callbackResult);
        } else if (typeof callbackResult === 'object') {
            const { ...data } = callbackResult;

            loadLanguage(callbackResult);
            sessionStorage.setItem(data.lcId);
        }
    }, []);

    const value = useMemo(() => ({ loading, language, locales, updateLanguage, updateLanguageWithData }), [
        loading,
        language,
        locales,
        updateLanguage,
        updateLanguageWithData,
    ]);
    return (
        <I18nContext.Provider value={value}>
            <RawIntlProvider value={intl}>
                <I18nBlurLoaderProvider value={loading}>{children}</I18nBlurLoaderProvider>
            </RawIntlProvider>
        </I18nContext.Provider>
    );
}

I18nProvider.propTypes = {
    children: PropTypes.node,
    isAppLoading: PropTypes.bool,
    locale: PropTypes.string
};

I18nProvider.defaultProps = {
    isAppLoading: false
};

export default I18nProvider;
